<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createConsumer } from '@rails/actioncable';
import QRCode from 'qrcode';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';
import dinastiaWhatsappChannel from 'dashboard/api/channel/dinastiaWhatsappChannel';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const connectionStatus = ref('');
const qrImage = ref('');
const isLoading = ref(false);
const isActionLoading = ref(false);
const isReconnecting = ref(false);
const isSyncing = ref(false);
const actionCooldown = ref(false);
const phoneNumber = ref('');
const groupsEnabled = ref(false);
const isTogglingGroups = ref(false);
const qrDialogRef = ref(null);
const proxyUrlMasked = ref(null);
const proxySource = ref(null);
const proxyPoolSource = ref(null);
const proxyConfigured = ref(false);
const proxyNeedsResync = ref(false);
const proxyLastAppliedMasked = ref(null);
const proxyInput = ref('');
const isEditingProxy = ref(false);
const isSavingProxy = ref(false);

const isAdmin = computed(() => {
  const role = store.getters.getCurrentUser?.role;
  return role === 'administrator';
});

const proxySourceLabel = computed(() => {
  const parts = [];
  if (proxySource.value === 'auto') {
    parts.push(t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_SOURCE_AUTO'));
  } else if (proxySource.value === 'manual') {
    parts.push(t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_SOURCE_MANUAL'));
  }
  if (proxyPoolSource.value === 'webshare') {
    parts.push(t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_POOL_WEBSHARE'));
  } else if (
    proxyPoolSource.value === 'manual' &&
    proxySource.value === 'auto'
  ) {
    parts.push(t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_POOL_MANUAL'));
  }
  return parts.join(' · ');
});
let pollTimer = null;
let autoCloseTimer = null;
let cableSubscription = null;
let cableConsumer = null;
let isInitialized = false;

// ── Low-level helpers (no dependencies) ────────────────────────────────

function channelId() {
  return props.inbox?.channel?.id || props.inbox?.channel_id;
}

async function processQrData(qr) {
  if (!qr) return;
  if (qr.startsWith('data:image/')) {
    qrImage.value = qr;
  } else {
    qrImage.value = await QRCode.toDataURL(qr, { width: 320, margin: 2 });
  }
}

function autoCloseDialog() {
  if (qrDialogRef.value) {
    if (autoCloseTimer) clearTimeout(autoCloseTimer);
    autoCloseTimer = setTimeout(() => {
      qrDialogRef.value?.close();
    }, 2500);
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function delay(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

// ── ActionCable (uses helpers above) ───────────────────────────────────

async function handleCableMessage(data) {
  switch (data.event) {
    case 'qr_update':
      await processQrData(data.data?.qr_code);
      break;
    case 'connection_update':
      connectionStatus.value = data.data?.status || data.status;
      if (data.data?.status === 'connected') {
        qrImage.value = '';
        stopPolling();
        autoCloseDialog();
      } else if (data.data?.status === 'disconnected') {
        stopPolling();
      } else if (data.data?.session_data?.qr) {
        await processQrData(data.data.session_data.qr);
      }
      break;
    case 'connection_message':
      if (data.data?.message) {
        useAlert(data.data.message);
      }
      break;
    default:
      break;
  }
}

function subscribeToChannel() {
  const chId = channelId();
  if (!chId) return;

  const { websocketURL = '' } = window.chatwootConfig || {};
  const wsUrl = websocketURL ? `${websocketURL}/cable` : undefined;
  cableConsumer = createConsumer(wsUrl);

  const userId = store.getters.getCurrentUserID;
  const currentUser = store.getters.getCurrentUser;
  const pubsubToken = currentUser?.pubsub_token;

  cableSubscription = cableConsumer.subscriptions.create(
    {
      channel: 'DinastiaWhatsappChannel',
      channel_id: chId,
      user_id: userId,
      pubsub_token: pubsubToken,
    },
    {
      received: receivedData => handleCableMessage(receivedData),
    }
  );
}

function unsubscribeFromChannel() {
  if (cableSubscription) {
    cableSubscription.unsubscribe();
    cableSubscription = null;
  }
  if (cableConsumer) {
    cableConsumer.disconnect();
    cableConsumer = null;
  }
}

// ── Status config ──────────────────────────────────────────────────────

const statusConfig = computed(() => {
  const configs = {
    connected: {
      color: 'bg-n-teal-9',
      pulseColor: 'bg-n-teal-9/40',
      textColor: 'text-n-teal-11',
      bgColor: 'bg-n-teal-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.CONNECTED'),
      icon: 'i-lucide-wifi',
      pulse: false,
    },
    disconnected: {
      color: 'bg-n-ruby-9',
      pulseColor: 'bg-n-ruby-9/40',
      textColor: 'text-n-ruby-11',
      bgColor: 'bg-n-ruby-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.DISCONNECTED'),
      icon: 'i-lucide-wifi-off',
      pulse: false,
    },
    qr_pending: {
      color: 'bg-n-amber-9',
      pulseColor: 'bg-n-amber-9/40',
      textColor: 'text-n-amber-11',
      bgColor: 'bg-n-amber-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.QR_PENDING'),
      icon: 'i-lucide-qr-code',
      pulse: true,
    },
    connecting: {
      color: 'bg-n-amber-9',
      pulseColor: 'bg-n-amber-9/40',
      textColor: 'text-n-amber-11',
      bgColor: 'bg-n-amber-3',
      label: t('INBOX_MGMT.DINASTIA_CONNECTION.CONNECTING'),
      icon: 'i-lucide-loader-2',
      pulse: true,
    },
  };
  return (
    configs[connectionStatus.value] || {
      color: 'bg-n-slate-7',
      pulseColor: 'bg-n-slate-7/40',
      textColor: 'text-n-slate-10',
      bgColor: 'bg-n-slate-3',
      label: '...',
      icon: 'i-lucide-circle-dashed',
      pulse: false,
    }
  );
});

const isConnected = computed(() => connectionStatus.value === 'connected');
const isNotConnected = computed(() => connectionStatus.value !== 'connected');
const hasQrPending = computed(
  () => connectionStatus.value === 'qr_pending' && !!qrImage.value
);

const dialogShowLoading = computed(() => {
  if (connectionStatus.value === 'connected') return false;
  if (connectionStatus.value === 'disconnected') return false;
  if (qrImage.value) return false;
  return true;
});

const dialogShowQr = computed(
  () => !!qrImage.value && connectionStatus.value !== 'connected'
);

const dialogShowConnected = computed(
  () => connectionStatus.value === 'connected'
);

// ── API actions ────────────────────────────────────────────────────────

async function fetchStatusForced() {
  if (!props.inbox.id) return;
  try {
    const { data } = await dinastiaWhatsappChannel.getSessionStatus(
      props.inbox.id
    );
    connectionStatus.value = data.connection_status;
    phoneNumber.value = data.phone_number || props.inbox.phone_number || '';

    if (data.session_data?.qr) {
      await processQrData(data.session_data.qr);
    } else if (data.connection_status === 'connected') {
      qrImage.value = '';
    }
  } catch {
    // Silently ignore polling errors
  }
}

function startFallbackPolling() {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    if (!cableSubscription) {
      fetchStatusForced();
    }
  }, 30000);
}

async function fetchStatus() {
  if (!props.inbox.id) return;
  if (isInitialized && cableSubscription) return;

  isLoading.value = true;
  try {
    const { data } = await dinastiaWhatsappChannel.getSessionStatus(
      props.inbox.id
    );
    connectionStatus.value = data.connection_status;
    phoneNumber.value = data.phone_number || props.inbox.phone_number || '';

    if (data.session_data?.qr) {
      await processQrData(data.session_data.qr);
    } else if (data.connection_status === 'connected') {
      qrImage.value = '';
    }

    if (
      data.connection_status === 'connected' ||
      data.connection_status === 'disconnected'
    ) {
      stopPolling();
    }

    if (data.connection_status === 'connected') {
      autoCloseDialog();
    }
  } catch {
    connectionStatus.value = props.inbox.connection_status || 'disconnected';
  } finally {
    isLoading.value = false;
    isInitialized = true;
  }
}

function startCooldown() {
  actionCooldown.value = true;
  setTimeout(() => {
    actionCooldown.value = false;
  }, 10000);
}

async function handleConnect() {
  if (actionCooldown.value) return;
  isActionLoading.value = true;
  qrImage.value = '';
  try {
    await dinastiaWhatsappChannel.connect(props.inbox.id);
    connectionStatus.value = 'connecting';
    startFallbackPolling();
  } catch {
    connectionStatus.value = 'disconnected';
  } finally {
    isActionLoading.value = false;
    startCooldown();
  }
}

async function handleDisconnect() {
  if (actionCooldown.value) return;
  isActionLoading.value = true;
  try {
    await dinastiaWhatsappChannel.disconnect(props.inbox.id);
    connectionStatus.value = 'disconnected';
    qrImage.value = '';
    stopPolling();
  } catch {
    // keep current status
  } finally {
    isActionLoading.value = false;
    startCooldown();
  }
}

async function handleReconnect() {
  if (actionCooldown.value) return;
  isReconnecting.value = true;
  qrDialogRef.value?.open();
  qrImage.value = '';
  try {
    await dinastiaWhatsappChannel.disconnect(props.inbox.id);
    connectionStatus.value = 'disconnected';
    await delay(2000);
    await dinastiaWhatsappChannel.connect(props.inbox.id);
    connectionStatus.value = 'connecting';
    startFallbackPolling();
  } catch {
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.RECONNECT_FAILED'));
  } finally {
    isReconnecting.value = false;
    startCooldown();
  }
}

async function handleSyncHistory() {
  isSyncing.value = true;
  try {
    await dinastiaWhatsappChannel.syncHistory(props.inbox.id);
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.SYNC_STARTED'));
  } catch {
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.SYNC_FAILED'));
  } finally {
    isSyncing.value = false;
  }
}

function openQrDialog() {
  qrDialogRef.value?.open();
  handleConnect();
}

function handleDialogClose() {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }
  if (connectionStatus.value !== 'connected') {
    stopPolling();
  }
}

// ── Lifecycle ──────────────────────────────────────────────────────────

watch(
  () => props.inbox.id,
  newId => {
    if (newId) {
      isInitialized = false;
      fetchStatus();
      unsubscribeFromChannel();
      subscribeToChannel();
    }
  },
  { immediate: true }
);

async function fetchProxyInfo() {
  if (!props.inbox.id) return;
  try {
    const { data } = await dinastiaWhatsappChannel.getProxyInfo(props.inbox.id);
    proxyUrlMasked.value = data.proxy_url_masked;
    proxySource.value = data.proxy_source;
    proxyPoolSource.value = data.pool_source;
    proxyConfigured.value = data.configured;
    proxyNeedsResync.value = data.needs_resync;
    proxyLastAppliedMasked.value = data.last_applied_url_masked;
  } catch {
    // ignore — silently keep current state
  }
}

function startEditingProxy() {
  proxyInput.value = '';
  isEditingProxy.value = true;
}

function cancelEditingProxy() {
  proxyInput.value = '';
  isEditingProxy.value = false;
}

async function saveProxy() {
  isSavingProxy.value = true;
  try {
    const { data } = await dinastiaWhatsappChannel.setManualProxy(
      props.inbox.id,
      proxyInput.value
    );
    proxyUrlMasked.value = data.proxy_url_masked;
    proxySource.value = data.proxy_source;
    proxyPoolSource.value = data.pool_source;
    proxyConfigured.value = data.configured;
    proxyNeedsResync.value = data.needs_resync;
    proxyLastAppliedMasked.value = data.last_applied_url_masked;
    isEditingProxy.value = false;
    proxyInput.value = '';
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_SAVED'));
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_SAVE_FAILED');
    useAlert(msg);
  } finally {
    isSavingProxy.value = false;
  }
}

async function clearProxy() {
  isSavingProxy.value = true;
  try {
    const { data } = await dinastiaWhatsappChannel.setManualProxy(
      props.inbox.id,
      ''
    );
    proxyUrlMasked.value = data.proxy_url_masked;
    proxySource.value = data.proxy_source;
    proxyPoolSource.value = data.pool_source;
    proxyConfigured.value = data.configured;
    proxyNeedsResync.value = data.needs_resync;
    proxyLastAppliedMasked.value = data.last_applied_url_masked;
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_CLEARED'));
  } catch {
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_SAVE_FAILED'));
  } finally {
    isSavingProxy.value = false;
  }
}

// ── Advanced settings (skip_*, echo, media, presence) ────────────────────
// Declared BEFORE toggleGroups so the two-sided toggle (groups + skip_groups)
// can read advancedSettings without a use-before-define lint error.
const advancedSettings = ref({
  skip_newsletters: true,
  skip_broadcasts: true,
  skip_groups: true,
  skip_calls: true,
  echo_api_messages: true,
  skip_media_download: false,
  skip_own_messages: false,
  auto_presence_unavailable: false,
});

const toggleGroups = async () => {
  isTogglingGroups.value = true;
  try {
    const { data } = await dinastiaWhatsappChannel.toggleGroups(
      props.inbox.id,
      !groupsEnabled.value
    );
    groupsEnabled.value = data.groups_enabled;
    advancedSettings.value.skip_groups = data.skip_groups;
    useAlert(
      groupsEnabled.value
        ? t('INBOX_MGMT.DINASTIA_CONNECTION.GROUPS_ENABLED')
        : t('INBOX_MGMT.DINASTIA_CONNECTION.GROUPS_DISABLED')
    );
  } catch {
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.GROUPS_TOGGLE_ERROR'));
  } finally {
    isTogglingGroups.value = false;
  }
};

const subscribedEvents = ref([]);
const unsupportedFlags = ref([]);
const settingsSource = ref('local');
const togglingFlag = ref(null);
const isApplyingEvents = ref(false);
const autoReconnectEnabled = ref(true);
const togglingAutoReconnect = ref(false);

async function fetchSettings() {
  if (!props.inbox.id) return;
  try {
    const { data } = await dinastiaWhatsappChannel.getSettings(props.inbox.id);
    if (data.settings) {
      advancedSettings.value = { ...advancedSettings.value, ...data.settings };
    }
    if (Array.isArray(data.subscribed_events)) {
      subscribedEvents.value = data.subscribed_events;
    }
    if (Array.isArray(data.unsupported_flags)) {
      unsupportedFlags.value = data.unsupported_flags;
    }
    settingsSource.value = data.source || 'local';
    if (typeof data.groups_enabled === 'boolean') {
      groupsEnabled.value = data.groups_enabled;
    }
    if (typeof data.auto_reconnect_enabled === 'boolean') {
      autoReconnectEnabled.value = data.auto_reconnect_enabled;
    }
  } catch {
    // ignore — UI keeps defaults
  }
}

async function onToggleAutoReconnect() {
  const newValue = !autoReconnectEnabled.value;
  autoReconnectEnabled.value = newValue;
  togglingAutoReconnect.value = true;
  try {
    const { data } = await dinastiaWhatsappChannel.toggleAutoReconnect(
      props.inbox.id,
      newValue
    );
    if (typeof data.auto_reconnect_enabled === 'boolean') {
      autoReconnectEnabled.value = data.auto_reconnect_enabled;
    }
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.FLAG_SAVED'));
  } catch (error) {
    autoReconnectEnabled.value = !newValue;
    const msg =
      error.response?.data?.error ||
      t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.FLAG_SAVE_FAILED');
    useAlert(msg);
  } finally {
    togglingAutoReconnect.value = false;
  }
}

async function onToggleFlag(flag) {
  // Optimistic update so the UI feels instant; rollback on failure.
  const newValue = !advancedSettings.value[flag];
  advancedSettings.value[flag] = newValue;
  togglingFlag.value = flag;
  try {
    const { data } = await dinastiaWhatsappChannel.toggleFlag(
      props.inbox.id,
      flag,
      newValue
    );
    if (data.settings) {
      advancedSettings.value = { ...advancedSettings.value, ...data.settings };
    }
    if (typeof data.groups_enabled === 'boolean') {
      groupsEnabled.value = data.groups_enabled;
    }
    useAlert(
      data.persisted_only
        ? t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.FLAG_PERSISTED_LOCAL')
        : t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.FLAG_SAVED')
    );
  } catch (error) {
    advancedSettings.value[flag] = !newValue;
    const msg =
      error.response?.data?.error ||
      t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.FLAG_SAVE_FAILED');
    useAlert(msg);
  } finally {
    togglingFlag.value = null;
  }
}

async function applyEvents() {
  isApplyingEvents.value = true;
  try {
    await dinastiaWhatsappChannel.syncEvents(props.inbox.id);
    useAlert(t('INBOX_MGMT.DINASTIA_CONNECTION.EVENTS_APPLIED'));
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      t('INBOX_MGMT.DINASTIA_CONNECTION.EVENTS_APPLY_FAILED');
    useAlert(msg);
  } finally {
    isApplyingEvents.value = false;
  }
}

function isUnsupportedFlag(flag) {
  return unsupportedFlags.value.includes(flag);
}

const advancedToggles = computed(() => [
  {
    key: 'skip_newsletters',
    label: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_NEWSLETTERS'),
    desc: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_NEWSLETTERS_DESC'),
  },
  {
    key: 'skip_broadcasts',
    label: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_BROADCASTS'),
    desc: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_BROADCASTS_DESC'),
  },
  {
    key: 'skip_calls',
    label: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_CALLS'),
    desc: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_CALLS_DESC'),
  },
  {
    key: 'echo_api_messages',
    label: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.ECHO_API_MESSAGES'),
    desc: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.ECHO_API_MESSAGES_DESC'),
  },
  {
    key: 'skip_media_download',
    label: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_MEDIA_DOWNLOAD'),
    desc: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_MEDIA_DOWNLOAD_DESC'),
  },
  {
    key: 'skip_own_messages',
    label: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_OWN_MESSAGES'),
    desc: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SKIP_OWN_MESSAGES_DESC'),
  },
  {
    key: 'auto_presence_unavailable',
    label: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.AUTO_PRESENCE'),
    desc: t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.AUTO_PRESENCE_DESC'),
  },
]);

onMounted(() => {
  subscribeToChannel();
  groupsEnabled.value = props.inbox?.provider_config?.groups_enabled === true;
  fetchProxyInfo();
  fetchSettings();
});

onUnmounted(() => {
  stopPolling();
  unsubscribeFromChannel();
  if (autoCloseTimer) clearTimeout(autoCloseTimer);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <SettingsSectionCard
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.TITLE')"
      :description="$t('INBOX_MGMT.DINASTIA_CONNECTION.DESCRIPTION')"
    >
      <div class="flex flex-col gap-5">
        <!-- Loading State -->
        <div
          v-if="isLoading && !connectionStatus"
          class="flex items-center justify-center p-8"
        >
          <span class="i-lucide-loader-2 animate-spin text-2xl text-n-brand" />
        </div>
        <!-- Status Card -->
        <div
          v-else
          class="flex items-center justify-between rounded-xl border border-n-weak p-4"
          :class="statusConfig.bgColor"
        >
          <div class="flex items-center gap-3">
            <!-- Status Dot -->
            <div class="relative flex items-center justify-center">
              <span
                v-if="statusConfig.pulse"
                class="absolute size-3 rounded-full animate-ping"
                :class="statusConfig.pulseColor"
              />
              <span
                class="relative size-3 rounded-full"
                :class="statusConfig.color"
              />
            </div>

            <div class="flex flex-col">
              <span
                class="text-sm font-semibold"
                :class="statusConfig.textColor"
              >
                {{ statusConfig.label }}
              </span>
              <span
                v-if="phoneNumber && isConnected"
                class="text-xs text-n-slate-10"
              >
                {{ phoneNumber }}
              </span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-2">
            <!-- Not connected: show connect or view QR -->
            <NextButton
              v-if="hasQrPending"
              solid
              amber
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.VIEW_QR')"
              icon="i-lucide-qr-code"
              @click="qrDialogRef?.open()"
            />
            <NextButton
              v-if="isNotConnected"
              solid
              teal
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.REQUEST_QR')"
              :is-loading="isActionLoading"
              :disabled="actionCooldown"
              icon="i-lucide-plug"
              @click="openQrDialog"
            />
            <!-- Connected: reconnect, sync, disconnect -->
            <NextButton
              v-if="isConnected"
              outline
              slate
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.RECONNECT')"
              :is-loading="isReconnecting"
              :disabled="actionCooldown"
              icon="i-lucide-refresh-cw"
              @click="handleReconnect"
            />
            <NextButton
              v-if="isConnected"
              outline
              slate
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.SYNC_HISTORY')"
              :is-loading="isSyncing"
              icon="i-lucide-history"
              @click="handleSyncHistory"
            />
            <NextButton
              v-if="isConnected"
              outline
              ruby
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.DISCONNECT')"
              :is-loading="isActionLoading"
              :disabled="actionCooldown"
              icon="i-lucide-unplug"
              @click="handleDisconnect"
            />
          </div>
        </div>
      </div>
    </SettingsSectionCard>

    <!-- Group Management Toggle -->
    <SettingsSectionCard
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.GROUP_MANAGEMENT')"
    >
      <div class="flex items-center justify-between">
        <div>
          <span class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.GROUP_MANAGEMENT') }}
          </span>
          <p class="text-xs text-n-slate-9 mt-0.5">
            {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.GROUP_MANAGEMENT_DESC') }}
          </p>
        </div>
        <input
          type="checkbox"
          :checked="groupsEnabled"
          :disabled="isTogglingGroups"
          class="cursor-pointer"
          @change="toggleGroups"
        />
      </div>
    </SettingsSectionCard>

    <!-- Configurações Avançadas (skip_*, echo, media, presence) -->
    <SettingsSectionCard
      v-if="isAdmin"
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.TITLE')"
      :description="$t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.DESCRIPTION')"
    >
      <div class="flex flex-col gap-4">
        <div
          v-for="toggle in advancedToggles"
          :key="toggle.key"
          class="flex items-start justify-between gap-4"
        >
          <div class="min-w-0 flex-1">
            <span class="text-sm text-n-slate-11">{{ toggle.label }}</span>
            <span
              v-if="isUnsupportedFlag(toggle.key)"
              class="ml-1 inline-flex items-center rounded-full bg-n-amber-3 px-2 py-0.5 text-[10px] font-medium text-n-amber-11"
              :title="
                $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.UNSUPPORTED_TOOLTIP')
              "
            >
              {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.UNSUPPORTED_BADGE') }}
            </span>
            <p class="text-xs text-n-slate-9 mt-0.5">{{ toggle.desc }}</p>
          </div>
          <input
            :checked="advancedSettings[toggle.key]"
            :disabled="togglingFlag === toggle.key"
            type="checkbox"
            class="cursor-pointer mt-1 shrink-0"
            @change="onToggleFlag(toggle.key)"
          />
        </div>

        <div class="border-t border-n-weak my-1" />

        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0 flex-1">
            <span class="text-sm text-n-slate-11">{{
              $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.AUTO_RECONNECT')
            }}</span>
            <span
              class="ml-1 inline-flex items-center rounded-full bg-n-teal-3 px-2 py-0.5 text-[10px] font-medium text-n-teal-11"
            >
              {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.LOCAL_BADGE') }}
            </span>
            <p class="text-xs text-n-slate-9 mt-0.5">
              {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.AUTO_RECONNECT_DESC') }}
            </p>
          </div>
          <input
            :checked="autoReconnectEnabled"
            :disabled="togglingAutoReconnect"
            type="checkbox"
            class="cursor-pointer mt-1 shrink-0"
            @change="onToggleAutoReconnect"
          />
        </div>

        <div class="border-t border-n-weak my-1" />

        <div
          v-if="subscribedEvents.length"
          class="rounded-md bg-n-slate-2 px-3 py-2 text-xs text-n-slate-10"
        >
          {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.EVENTS_LIST_LABEL') }}
          <code class="text-n-slate-12 ml-1">
            {{ subscribedEvents.join(', ') }}
          </code>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="text-xs text-n-slate-9 mb-0">
            {{
              settingsSource === 'dinastia'
                ? $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SOURCE_DINASTIA')
                : $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.SOURCE_LOCAL')
            }}
          </p>
          <NextButton
            outline
            teal
            sm
            :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.APPLY_EVENTS')"
            :is-loading="isApplyingEvents"
            icon="i-lucide-refresh-cw"
            @click="applyEvents"
          />
        </div>

        <p class="text-xs text-n-slate-9">
          {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.ADV.FOOTER_HINT') }}
        </p>
      </div>
    </SettingsSectionCard>

    <!-- Proxy Configuration (admin only) -->
    <SettingsSectionCard
      v-if="isAdmin"
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_TITLE')"
      :description="$t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_DESCRIPTION')"
    >
      <div class="flex flex-col gap-4">
        <!-- Current proxy display -->
        <div
          v-if="!isEditingProxy"
          class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-slate-2 p-3"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              <span
                class="size-2 rounded-full shrink-0"
                :class="proxyConfigured ? 'bg-n-teal-9' : 'bg-n-slate-7'"
              />
              <div class="flex flex-col min-w-0">
                <span
                  v-if="proxyConfigured"
                  class="text-sm font-mono text-n-slate-12 truncate"
                >
                  {{ proxyUrlMasked }}
                </span>
                <span v-else class="text-sm text-n-slate-10">
                  {{
                    $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_NONE_CONFIGURED')
                  }}
                </span>
                <span
                  v-if="proxyConfigured && proxySourceLabel"
                  class="text-xs text-n-slate-9 mt-0.5"
                >
                  {{ proxySourceLabel }}
                </span>
              </div>
            </div>
            <div class="flex gap-2 shrink-0">
              <NextButton
                outline
                slate
                sm
                :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_EDIT')"
                icon="i-lucide-pencil"
                @click="startEditingProxy"
              />
              <NextButton
                v-if="proxyConfigured"
                outline
                ruby
                sm
                :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_CLEAR')"
                icon="i-lucide-trash-2"
                :is-loading="isSavingProxy"
                @click="clearProxy"
              />
            </div>
          </div>
          <div
            v-if="proxyNeedsResync"
            class="flex items-start gap-2 rounded-md bg-n-amber-3 border border-n-amber-6 p-2.5"
          >
            <span
              class="i-lucide-alert-triangle text-n-amber-11 shrink-0 mt-0.5"
            />
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-medium text-n-amber-11">
                {{
                  $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_OUT_OF_SYNC_TITLE')
                }}
              </span>
              <span class="text-xs text-n-amber-11/90">
                {{
                  $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_OUT_OF_SYNC_DESC')
                }}
              </span>
              <span
                v-if="proxyLastAppliedMasked"
                class="text-xs font-mono text-n-amber-11/80 mt-0.5"
              >
                {{
                  $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_OUT_OF_SYNC_LAST')
                }}
                {{ proxyLastAppliedMasked }}
              </span>
            </div>
          </div>
        </div>

        <!-- Edit form -->
        <div v-else class="flex flex-col gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_URL_LABEL') }}
            </span>
            <input
              v-model="proxyInput"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_URL_PLACEHOLDER')
              "
              class="w-full rounded-md border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm font-mono text-n-slate-12 focus:border-n-brand focus:outline-none"
              :disabled="isSavingProxy"
            />
            <span class="text-xs text-n-slate-9">
              {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_URL_HINT') }}
            </span>
          </label>
          <div class="flex justify-end gap-2">
            <NextButton
              outline
              slate
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_CANCEL')"
              :disabled="isSavingProxy"
              @click="cancelEditingProxy"
            />
            <NextButton
              solid
              teal
              sm
              :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_SAVE')"
              icon="i-lucide-check"
              :is-loading="isSavingProxy"
              :disabled="!proxyInput.trim()"
              @click="saveProxy"
            />
          </div>
        </div>

        <p class="text-xs text-n-slate-9">
          {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.PROXY_NOTE') }}
        </p>
      </div>
    </SettingsSectionCard>

    <!-- QR Code Dialog -->
    <Dialog
      ref="qrDialogRef"
      :title="$t('INBOX_MGMT.DINASTIA_CONNECTION.QR_DIALOG_TITLE')"
      :description="$t('INBOX_MGMT.DINASTIA_CONNECTION.QR_DIALOG_DESCRIPTION')"
      width="md"
      :show-confirm-button="false"
      :show-cancel-button="false"
      @close="handleDialogClose"
    >
      <div class="flex flex-col items-center gap-6 py-2">
        <!-- Dialog: Loading -->
        <div
          v-if="dialogShowLoading"
          class="flex flex-col items-center gap-4 py-6"
        >
          <span class="i-lucide-loader-2 animate-spin text-4xl text-n-brand" />
          <p class="text-sm text-n-slate-10 mb-0">
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_WAITING') }}
          </p>
        </div>

        <!-- Dialog: QR Code -->
        <div v-else-if="dialogShowQr" class="flex flex-col items-center gap-4">
          <div class="rounded-xl bg-white p-3 shadow-sm">
            <img :src="qrImage" alt="WhatsApp QR Code" class="size-64" />
          </div>
          <div class="flex items-center gap-1.5 text-xs text-n-slate-8">
            <span class="i-lucide-refresh-cw text-xs" />
            <span>{{ $t('INBOX_MGMT.FINISH.DINASTIA_QR_REFRESHES') }}</span>
          </div>
          <button
            type="button"
            class="text-xs text-n-brand hover:text-n-brand/80 transition-colors"
            @click="handleConnect"
          >
            {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.REGENERATE_QR') }}
          </button>
        </div>

        <!-- Dialog: Connected -->
        <div
          v-else-if="dialogShowConnected"
          class="flex flex-col items-center gap-4 py-4"
        >
          <div
            class="flex items-center justify-center size-16 rounded-full bg-n-teal-3"
          >
            <span class="i-lucide-check text-3xl text-n-teal-11" />
          </div>
          <div class="text-center">
            <p class="text-base font-semibold text-n-teal-11 mb-1">
              {{ $t('INBOX_MGMT.DINASTIA_CONNECTION.CONNECTED_SUCCESS') }}
            </p>
            <p v-if="phoneNumber" class="text-sm text-n-slate-10 mb-0">
              {{ phoneNumber }}
            </p>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-center">
          <NextButton
            v-if="dialogShowConnected"
            solid
            teal
            :label="$t('INBOX_MGMT.DINASTIA_CONNECTION.CLOSE')"
            class="w-full"
            @click="qrDialogRef?.close()"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
